import React, { useState, useEffect } from 'react';
import StandardModal from './StandardModal';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { AlertCircle, Package, Users, Clock, User, Phone, Mail, Plus, X } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './Buttons';
import TimeSlotSelector from './TimeSlotSelector';
import RoleSelector from './RoleSelector';
import { toast } from 'sonner';

/**
 * Edit Booking Modal - Airplane Ticket Style
 * Validates all changes before saving
 * Shows conflicts and suggestions
 */
const EditBookingModal = ({
  isOpen,
  onClose,
  trip,
  assets,
  teamMembers,
  roles,
  allBookings,
  onSave,
  timeFormat
}) => {
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    duration: 60,
    assetId: '',
    teamAssignments: [],
    customerName: '',
    customerPhone: '',
    customerEmail: ''
  });
  
  const [conflicts, setConflicts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  // Initialize form data when trip changes
  useEffect(() => {
    if (trip) {
      setFormData({
        date: trip.date || '',
        startTime: trip.startTime || '',
        duration: trip.duration || 60,
        assetId: trip.assetId || '',
        teamAssignments: trip.teamAssignments || [],
        customerName: trip.customerName || '',
        customerPhone: trip.customerPhone || '',
        customerEmail: trip.customerEmail || ''
      });
    }
  }, [trip]);

  // Helper: Convert time to minutes
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper: Check time overlap
  const checkTimeOverlap = (trip1Start, trip1Duration, trip2Start, trip2Duration) => {
    const start1 = timeToMinutes(trip1Start);
    const end1 = start1 + trip1Duration;
    const start2 = timeToMinutes(trip2Start);
    const end2 = start2 + trip2Duration;
    
    return start1 < end2 && end1 > start2;
  };

  // Check for conflicts whenever form changes
  useEffect(() => {
    if (!formData.startTime || !formData.date) return;

    const detectedConflicts = [];
    const availableSuggestions = [];

    // Check asset conflicts
    if (formData.assetId && formData.assetId !== 'unassigned') {
      const assetConflicts = allBookings.filter(t =>
        t.id !== trip?.id &&
        t.date === formData.date &&
        t.assetId === formData.assetId &&
        checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
      );

      if (assetConflicts.length > 0) {
        const asset = assets.find(b => b.id === formData.assetId);
        detectedConflicts.push({
          type: 'asset',
          message: `${asset?.name} is booked ${assetConflicts[0].startTime} - ${calculateEndTime(assetConflicts[0].startTime, assetConflicts[0].duration)}`
        });

        // Suggest available assets
        const availableAssets = assets.filter(b =>
          b.active &&
          !allBookings.some(t =>
            t.id !== trip?.id &&
            t.date === formData.date &&
            t.assetId === b.id &&
            checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
          )
        );

        if (availableAssets.length > 0) {
          availableSuggestions.push({
            type: 'asset',
            items: availableAssets.slice(0, 3).map(v => ({ id: v.id, name: v.name }))
          });
        }
      }
    }

    // Check team conflicts
    formData.teamAssignments.forEach((assignment, idx) => {
      if (assignment.teamId) {
        const teamConflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === assignment.teamId) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );

        if (teamConflicts.length > 0) {
          const team = teamMembers.find(c => c.id === assignment.teamId);
          detectedConflicts.push({
            type: 'team',
            message: `${team?.name} is booked ${teamConflicts[0].startTime} - ${calculateEndTime(teamConflicts[0].startTime, teamConflicts[0].duration)}`
          });
        }
      }
    });

    setConflicts(detectedConflicts);
    setSuggestions(availableSuggestions);
  }, [formData, allBookings, trip, assets, teamMembers]);

  // Helper: Calculate end time
  const calculateEndTime = (startTime, duration) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + duration;
    const hours = Math.floor(endMinutes / 60) % 24;
    const mins = endMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Handle asset change - auto-populate team
  const handleAssetChange = (assetId) => {
    setFormData(prev => ({ ...prev, assetId: assetId }));

    if (assetId !== 'unassigned' && assetId) {
      const selectedAsset = assets.find(b => b.id === assetId);
      if (selectedAsset?.teamRequirements) {
        const autoTeamSlots = [];
        selectedAsset.teamRequirements.forEach(req => {
          for (let i = 0; i < req.quantity; i++) {
            autoTeamSlots.push({
              teamId: '',
              teamName: '',
              roleId: req.roleId,
              roleName: req.roleName
            });
          }
        });
        setFormData(prev => ({ ...prev, teamAssignments: autoTeamSlots }));
      }
    }
  };

  // Handle save
  const handleSave = async () => {
    // Show conflicts as toast if any
    if (conflicts.length > 0) {
      const confirmSave = window.confirm(
        `Warning: ${conflicts.length} conflict(s) detected:\n${conflicts.map(c => c.message).join('\n')}\n\nDo you want to save anyway?`
      );
      if (!confirmSave) return;
    }

    await onSave(trip.id, formData);
    onClose();
    toast.success('Booking updated successfully!');
  };

  // Get ALL assets with availability status
  const getAssetsWithStatus = () => {
    const assignedTeamIds = formData.teamAssignments
      .map(a => a.teamId)
      .filter(id => id);

    return assets.map(asset => {
      let available = true;
      let reason = '';

      // Check if active
      if (!asset.active) {
        available = false;
        reason = 'Inactive';
      }
      
      // Check team compatibility
      if (available && assignedTeamIds.length > 0) {
        const allowedTeam = asset.allowedTeam || [];
        if (allowedTeam.length > 0) {
          const allAllowed = assignedTeamIds.every(id => allowedTeam.includes(id));
          if (!allAllowed) {
            available = false;
            reason = 'Team not allowed on this asset';
          }
        }
      }

      // Check time conflicts
      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.assetId === asset.id &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Already booked ${conflicts[0].startTime}`;
        }
      }

      return { ...asset, available, reason };
    });
  };

  // Get ALL team for a role with availability status
  const getTeamWithStatus = (roleId) => {
    return teamMembers.map(c => {
      let available = true;
      let reason = '';

      // Check role match
      if (c.roleId !== roleId) {
        available = false;
        const teamRole = roles.find(r => r.id === c.roleId);
        reason = `${teamRole?.name || 'Different role'}`;
      }

      // Check asset compatibility
      if (available && formData.assetId && formData.assetId !== 'unassigned') {
        const asset = assets.find(b => b.id === formData.assetId);
        const allowedTeam = asset?.allowedTeam || [];
        if (allowedTeam.length > 0 && !allowedTeam.includes(c.id)) {
          available = false;
          reason = 'Not allowed on this asset';
        }
      }

      // Check time conflicts
      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === c.id) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Already booked ${conflicts[0].startTime}`;
        }
      }

      return { ...c, available, reason };
    });
  };

  if (!trip) return null;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Booking"
      description="Update trip details and assignments"
      size="lg"
      footer={
        <>
          <SecondaryButton onClick={onClose} className="flex-1 sm:flex-none">
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} className="flex-1 sm:flex-none">
            Save Changes
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-6">
        {/* Conflicts Warning */}
        {conflicts.length > 0 && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-800 mb-1">Conflicts Detected</p>
                {conflicts.map((conflict, idx) => (
                  <p key={idx} className="text-xs text-orange-700">• {conflict.message}</p>
                ))}
              </div>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && suggestions[0].type === 'asset' && (
              <div className="mt-2 pt-2 border-t border-orange-200">
                <p className="text-xs font-semibold text-orange-800 mb-1">Available Assets:</p>
                <div className="flex flex-wrap gap-1">
                  {suggestions[0].items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleAssetChange(item.id)}
                      className="px-2 py-1 bg-white border border-orange-300 rounded text-xs text-orange-700 hover:bg-orange-100"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Time and Duration */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1 mb-2">
              <Clock className="w-4 h-4" />
              Start Time *
            </Label>
            <TimeSlotSelector
              value={formData.startTime}
              onChange={(time) => setFormData(prev => ({ ...prev, startTime: time }))}
            />
          </div>

          <div>
            <Label className="mb-2 block">Duration (minutes) *</Label>
            <Select
              value={String(formData.duration)}
              onValueChange={(val) => setFormData(prev => ({ ...prev, duration: parseInt(val) }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
                <SelectItem value="240">4 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Asset Selection */}
        <div>
          <Label className="flex items-center gap-1 mb-2">
            <Package className="w-4 h-4" />
            Asset
          </Label>
          <Select
            value={formData.assetId || 'unassigned'}
            onValueChange={handleAssetChange}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {getAssetsWithStatus().map(asset => (
                <SelectItem 
                  key={asset.id} 
                  value={asset.id}
                  disabled={!asset.available}
                  className={!asset.available ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  {asset.name}
                  {!asset.available && ` (${asset.reason})`}
                </SelectItem>
              ))}
              <div className="border-t mt-1 pt-1">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent('navigate', { detail: 'assets' }));
                  }}
                  className="w-full text-left px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Asset
                </button>
              </div>
            </SelectContent>
          </Select>
          {formData.teamAssignments.some(a => a.teamId) && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Showing all assets (unavailable greyed out)
            </p>
          )}
        </div>

        {/* Team Assignments */}
        <div>
          <Label className="flex items-center gap-1 mb-2">
            <Users className="w-4 h-4" />
            Team ({formData.teamAssignments.length})
          </Label>
          {formData.teamAssignments.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded border border-dashed">
              Select a asset to auto-populate team roles
            </p>
          ) : (
            <div className="space-y-2">
              {formData.teamAssignments.map((assignment, idx) => {
                const role = roles.find(r => r.id === assignment.roleId);
                const roleColor = role?.color || '#6B7280';

                return (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: roleColor }}
                      />
                      <span className="text-xs font-semibold text-gray-700">
                        {assignment.roleName}
                      </span>
                    </div>

                    <Select
                      value={assignment.teamId || 'none'}
                      onValueChange={(teamId) => {
                        const updated = [...formData.teamAssignments];
                        if (teamId === 'none') {
                          updated[idx] = { ...updated[idx], teamId: '', teamName: '' };
                        } else {
                          const selectedTeam = teamMembers.find(c => c.id === teamId);
                          updated[idx] = {
                            ...updated[idx],
                            teamId: teamId,
                            teamName: selectedTeam?.name || ''
                          };
                        }
                        setFormData(prev => ({ ...prev, teamAssignments: updated }));
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>
                          {assignment.teamName || <span className="text-gray-400">Select team...</span>}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {getTeamWithStatus(assignment.roleId).map(team => (
                          <SelectItem 
                            key={team.id} 
                            value={team.id}
                            disabled={!team.available}
                            className={!team.available ? 'opacity-50 cursor-not-allowed' : ''}
                          >
                            {team.name}
                            {!team.available && ` (${team.reason})`}
                          </SelectItem>
                        ))}
                        <div className="border-t mt-1 pt-1">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.dispatchEvent(new CustomEvent('navigate', { detail: 'teamMembers' }));
                            }}
                            className="w-full text-left px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Add Team
                          </button>
                        </div>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
          {formData.assetId && formData.assetId !== 'unassigned' && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Showing all team (unavailable greyed out)
            </p>
          )}
        </div>

        {/* Customer Information */}
        <div>
          <Label className="flex items-center gap-1 mb-2">
            <User className="w-4 h-4" />
            Customer Information (Optional)
          </Label>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-600">Name</Label>
              <Input
                value={formData.customerName}
                onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Customer name"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-600 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  Phone
                </Label>
                <Input
                  value={formData.customerPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                  placeholder="customer@example.com"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </StandardModal>
  );
};

export default EditBookingModal;
